import React, { useMemo, useState } from "react";
import {
  Drawer, Box, Typography, IconButton, Chip, Avatar, Button, Skeleton, Tooltip,
  LinearProgress, Menu, MenuItem, ListItemIcon, ListItemText, Divider, alpha, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventSeatOutlinedIcon from "@mui/icons-material/EventSeatOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useGetScheduleDetailsQuery } from "../../../Api/attoDeskApi";
import { statusMetaFor } from "./statusMeta";
import { GradeSubjectsSummary } from "../../../Components/Subjects/SubjectCollection";
import dayjs from "dayjs";

type Gate = boolean | { on: boolean; why?: string };

export interface DetailsPermissions {
  edit?: Gate;
  attendance?: Gate;
  assignStudent?: Gate;
  switchStaff?: Gate;
  viewStudent?: Gate;
  viewStaff?: Gate;
  release?: Gate;
  /** Per-student removal from this occurrence ("Turn Out" in the UI). */
  turnOut?: Gate;
  scheduleMakeup?: Gate;
}

const gate = (g?: Gate): { on: boolean; why: string } =>
  typeof g === "object" && g !== null ? { on: !!g.on, why: g.why || "" } : { on: !!g, why: "" };

const fmtDateFull = (d?: string) => (d ? dayjs(d).format("dddd, MMMM D, YYYY") : "—");
const fmtDateTime = (iso?: string) => (iso ? dayjs(iso).format("MMM D, YYYY [at] h:mm A") : "—");
const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const dt = new Date();
  dt.setHours(h, m);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(dt);
};
const fmtDuration = (min?: number | null) => {
  if (!min) return "Not set";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h} hour${h === 1 ? "" : "s"}` : "", m ? `${m} min` : ""].filter(Boolean).join(" ") || "Not set";
};
const initials = (name: string) =>
  (name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("") || "?";

const NAVY = "#0f172a";
const SLATE = "#475569";
const MUTED = "#64748b";

const ScheduleDetailsDrawer: React.FC<{
  open: boolean;
  occurrenceId: number | string | null;
  fallbackRow?: any;
  can?: DetailsPermissions;
  onClose: () => void;
  onAction?: (type: string, payload?: any) => void;
}> = ({ open, occurrenceId, fallbackRow, can = {}, onClose, onAction }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { currentData, isFetching, isError, refetch } = useGetScheduleDetailsQuery(occurrenceId as any, {
    skip: !open || occurrenceId == null,
  });

  // Only trust data that matches the requested occurrence — guards against a stale
  // response from a previously-selected row (never show the old row while loading).
  const d: any = useMemo(() => {
    const payload = (currentData as any)?.data;
    if (!payload) return null;
    return String(payload.session?.occurrence_id) === String(occurrenceId) ? payload : null;
  }, [currentData, occurrenceId]);

  const loading = isFetching || !d;
  const status = d ? statusMetaFor({ status: d.session?.status, scheduleType: d.session?.schedule_type }) : null;

  const [moreEl, setMoreEl] = useState<null | HTMLElement>(null);

  const gEdit = gate(can.edit);
  const gSwitch = gate(can.switchStaff);
  const gAssign = gate(can.assignStudent);
  const gAtt = gate(can.attendance);
  const gViewStudent = gate(can.viewStudent);
  const gViewStaff = gate(can.viewStaff);
  const gRelease = gate(can.release);
  const gTurnOut = gate(can.turnOut);
  const gMakeup = gate(can.scheduleMakeup);

  // ── shared visual atoms ──
  const Card: React.FC<{ icon: React.ReactNode; title: React.ReactNode; tint?: string; children: React.ReactNode; action?: React.ReactNode }> = ({ icon, title, tint = primary, children, action }) => (
    <Box sx={{ bgcolor: "#fff", border: "1px solid rgba(15,23,42,0.08)", borderRadius: "14px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: alpha(tint, 0.12), color: tint, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 16 } }}>{icon}</Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
        </Box>
        {action}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );

  // Stacked label→value block (label above value; never far-apart across the drawer).
  // Colours are hard-coded to slate/navy so they cannot resolve to
  // `#fff` even if a parent theme flips `text.primary` / `text.secondary`
  // to light values (dark mode contexts, admin-preview overlays, etc.).
  const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, mb: 0.35 }}>{label}</Typography>
      <Box sx={{ fontSize: 13, fontWeight: 600, color: NAVY, "& *": { fontSize: 13, color: "inherit" } }}>{children}</Box>
    </Box>
  );

  const softChip = (label: string, tint: string) => (
    <Chip label={label} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(tint, 0.12), color: tint, borderRadius: "7px" }} />
  );

  const enrollTint = (s?: string) => (s === "Full" ? theme.palette.error.main : s === "No students assigned" ? theme.palette.warning.main : theme.palette.success.main);

  // ── Quick action button (disabled ⇒ tooltip reason) ──
  // Outlined variant gets explicit dark navy text + a soft navy border
  // so the label is always readable on the drawer's #f8fafc surface,
  // regardless of what the ambient theme.primary resolves to. The
  // contained (primary) variant keeps its white label on the dark fill.
  const QA: React.FC<{ icon: React.ReactNode; label: string; g: { on: boolean; why: string }; onClick: () => void; primaryBtn?: boolean }> = ({ icon, label, g, onClick, primaryBtn }) => {
    const outlinedSx = {
      color: NAVY,
      borderColor: alpha(NAVY, 0.22),
      bgcolor: "#fff",
      "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
      "& .MuiButton-startIcon": { color: NAVY },
      "&.Mui-disabled": { color: alpha(NAVY, 0.4), borderColor: alpha(NAVY, 0.12) },
    };
    const btn = (
      <Button
        size="small"
        variant={primaryBtn ? "contained" : "outlined"}
        startIcon={icon}
        disabled={!g.on}
        onClick={onClick}
        sx={{
          textTransform: "none",
          borderRadius: "9px",
          fontWeight: 700,
          flex: { xs: "1 1 45%", sm: "0 0 auto" },
          whiteSpace: "nowrap",
          ...(primaryBtn ? {} : outlinedSx),
        }}
      >
        {label}
      </Button>
    );
    return !g.on && g.why ? <Tooltip title={g.why}><span style={{ display: "inline-flex", flex: "inherit" as any }}>{btn}</span></Tooltip> : btn;
  };

  // ── Overflow-menu item (disabled ⇒ tooltip reason, keyboard-accessible) ──
  const MoreItem: React.FC<{ icon: React.ReactNode; label: string; g: { on: boolean; why: string }; danger?: boolean; onClick: () => void }> = ({ icon, label, g, danger, onClick }) => {
    const item = (
      <MenuItem disabled={!g.on} onClick={() => { setMoreEl(null); onClick(); }} sx={{ fontSize: 13, py: 0.9, ...(danger ? { color: "error.main" } : {}) }}>
        <ListItemIcon sx={{ color: danger ? "error.main" : "inherit", minWidth: "34px !important" }}>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </MenuItem>
    );
    return !g.on && g.why ? <Tooltip title={g.why} placement="left"><span>{item}</span></Tooltip> : item;
  };

  // ── Summary stat card ──
  const Stat: React.FC<{ icon: React.ReactNode; value: React.ReactNode; label: string; tint: string }> = ({ icon, value, label, tint }) => (
    <Box sx={{ bgcolor: alpha(tint, 0.06), border: `1px solid ${alpha(tint, 0.16)}`, borderRadius: "12px", p: 1.25, display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      <Box sx={{ width: 24, height: 24, borderRadius: "7px", bgcolor: alpha(tint, 0.14), color: tint, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 } }}>{icon}</Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.1, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</Typography>
      <Typography sx={{ fontSize: 10, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</Typography>
    </Box>
  );

  const paperSx = {
    width: { xs: "100%", sm: "78vw", md: 560 },
    maxWidth: 580,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    bgcolor: "#f8fafc",
    // Baseline dark colour for the drawer so any descendant Typography
    // without an explicit `color` inherits a readable navy. The actual
    // root cause of the previous "white on white" bug lived in
    // theme/saas-overrides.css (a `.MuiDrawer-paper .MuiTypography-root`
    // rule that forced `color: #fff !important` on every drawer); that
    // stylesheet now scopes those rules to left-anchor drawers only.
    color: NAVY,
    boxShadow: "-10px 0 40px rgba(15,23,42,0.14)",
    overflow: "hidden", // the inner body owns the scroll; paper never scrolls
  } as const;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: paperSx }}
      ModalProps={{ keepMounted: false }}
    >
      {/* ── Sticky header (staff-first) ─────────────────────────────────
          Staff name is the largest, most prominent line — the person
          teaching this specific dated session is what admins scan for
          first. Class name / grade+subject moves to the subtitle rank;
          date + time sit below. A soft brand gradient supplies the
          premium feel without depending on a per-location colour prop. */}
      <Box sx={{
        flexShrink: 0, px: 2.5, pt: 2.25, pb: 1.75,
        background: `linear-gradient(135deg, ${alpha(primary, 0.14)} 0%, ${alpha("#0ea5e9", 0.08)} 60%, ${alpha(primary, 0.03)} 100%)`,
        borderBottom: `1px solid ${alpha(primary, 0.14)}`,
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
          {loading && !d ? (
            <>
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton width="70%" height={30} />
                <Skeleton width="50%" height={16} />
                <Skeleton width="60%" height={16} />
              </Box>
            </>
          ) : d ? (
            <>
              <Avatar
                sx={{
                  width: 56, height: 56, flexShrink: 0,
                  fontSize: 20, fontWeight: 800,
                  bgcolor: alpha(primary, 0.15), color: primary,
                  border: `2px solid ${alpha(primary, 0.25)}`,
                  boxShadow: `0 6px 18px -8px ${alpha(primary, 0.5)}`,
                }}
              >
                {d.staff?.name ? initials(d.staff.name) : "?"}
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                {/* 1. Staff name — largest line, high-weight, dominant. */}
                <Typography
                  sx={{
                    fontSize: 20, fontWeight: 800, lineHeight: 1.15,
                    color: NAVY, letterSpacing: "-0.01em",
                    wordBreak: "break-word",
                  }}
                >
                  {d.staff?.name || "Unassigned"}
                </Typography>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, mt: 0.15 }}>
                  Teacher{d.staff?.location ? ` · ${d.staff.location}` : ""}
                </Typography>
                {/* 2. Class / grade+subject — secondary, still readable. */}
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: NAVY, mt: 0.75 }}>
                  {d.class?.display_name || "Multi-grade scheduled class"}
                </Typography>
                {/* 3. Date + time. */}
                <Typography sx={{ fontSize: 12, color: SLATE, mt: 0.1 }}>
                  {fmtDateFull(d.session.scheduled_date)}
                  {" · "}
                  <Box component="span" sx={{ fontWeight: 700, color: NAVY }}>
                    {fmtTime(d.session.start_time)}–{fmtTime(d.session.end_time)}
                  </Box>
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Class Session Details</Typography>
              <Typography sx={{ fontSize: 12, color: SLATE, mt: 0.25 }}>Loading session…</Typography>
            </Box>
          )}
          <IconButton size="small" aria-label="Close details" onClick={onClose} sx={{ mt: -0.75, mr: -0.75 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {d && (
          <Box sx={{ display: "flex", gap: 0.75, mt: 1.5, flexWrap: "wrap" }}>
            {status && (
              <Chip
                size="small"
                icon={<CircleIcon sx={{ fontSize: "9px !important", color: `${status.color} !important` }} />}
                label={status.label}
                sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: status.bg, color: status.text, borderRadius: "8px", pl: 0.5 }}
              />
            )}
            {softChip(d.session.is_recurring ? "Recurring" : "One-off", d.session.is_recurring ? "#f59e0b" : primary)}
            {d.class?.is_online
              ? softChip("Online", "#0ea5e9")
              : d.class?.location && softChip(d.class.location, "#8b5cf6")}
          </Box>
        )}
      </Box>

      {/* ── Sticky action bar — grouped Session vs Schedule ─────────────
          Occurrence-scoped actions (Switch Staff for this date, Assign
          Student to this occurrence, Mark Attendance, Turn Out, Makeup)
          are visually distinct from schedule-scoped actions (Edit the
          recurring session pattern). Each disabled button carries a
          tooltip reason so users are never left staring at a greyed
          control with no explanation. */}
      {d && (
        <Box sx={{ flexShrink: 0, px: 2.25, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid rgba(15,23,42,0.08)", display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
              Session Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <QA icon={<SwapHorizIcon />} label="Switch Staff" g={gSwitch} onClick={() => onAction?.("switchStaff")} primaryBtn />
              <QA icon={<PersonAddAlt1OutlinedIcon />} label="Assign Student" g={gAssign} onClick={() => onAction?.("assignStudent")} />
              <QA icon={<FactCheckOutlinedIcon />} label="Attendance" g={gAtt} onClick={() => onAction?.("attendance")} />
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="More session actions">
                <IconButton size="small" aria-label="More session actions" aria-haspopup="menu" onClick={(e) => setMoreEl(e.currentTarget)} sx={{ border: "1px solid rgba(15,23,42,0.12)", borderRadius: "9px" }}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
              Schedule Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <QA icon={<EditSharpIcon />} label="Edit Schedule" g={gEdit} onClick={() => onAction?.("edit")} />
              {/* View Full Details → the canonical schedule page. Uses
                  class_schedule_id (via the row's scheduleId/classId
                  fallback, resolved by the parent). Never occurrence_id. */}
              <QA icon={<VisibilityOutlinedIcon />} label="View Full Details" g={{ on: true, why: "" }} onClick={() => onAction?.("viewFullDetails")} />
            </Box>
          </Box>
          <Menu anchorEl={moreEl} open={Boolean(moreEl)} onClose={() => setMoreEl(null)} transformOrigin={{ horizontal: "right", vertical: "top" }} anchorOrigin={{ horizontal: "right", vertical: "bottom" }} slotProps={{ paper: { sx: { minWidth: 200, borderRadius: "10px" } } }}>
            <MoreItem icon={<EventRepeatOutlinedIcon fontSize="small" />} label="Schedule Makeup" g={gMakeup} onClick={() => onAction?.("scheduleMakeup")} />
            <MoreItem icon={<PersonRemoveOutlinedIcon fontSize="small" />} label="Turn Out (remove student)" g={gTurnOut} onClick={() => onAction?.("turnOut")} />
            <Divider sx={{ my: 0.5 }} />
            <MoreItem icon={<EventBusyOutlinedIcon fontSize="small" />} label="Release / Cancel Class" g={gRelease} onClick={() => onAction?.("release")} />
          </Menu>
        </Box>
      )}

      {isFetching && <LinearProgress sx={{ flexShrink: 0 }} />}

      {/* ── Scrollable content body ──
          The scroll region is a plain block flex-child with min-height:0 (so a flex
          column parent lets it shrink and actually scroll); the section stack lives
          in an INNER auto-height flex column, so cards keep their natural height and
          are never compressed. Generous bottom padding keeps the last card clear of
          the chat launcher. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        {isError ? (
          <Box sx={{ mt: 6, textAlign: "center", px: 3, pb: 4 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: alpha(theme.palette.error.main, 0.1), display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <ErrorOutlineIcon sx={{ fontSize: 30, color: theme.palette.error.main }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>Unable to load schedule details.</Typography>
            <Typography sx={{ fontSize: 12.5, color: SLATE, mb: 2.5 }}>Something went wrong while fetching this session.</Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => refetch()} sx={{ textTransform: "none", borderRadius: "9px" }}>Retry</Button>
              <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", borderRadius: "9px" }}>Close</Button>
            </Box>
          </Box>
        ) : loading ? (
          <Box sx={{ p: 2.25, pb: 5, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1.25 }}>
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={78} sx={{ borderRadius: "12px" }} />)}
            </Box>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: "14px" }} />)}
          </Box>
        ) : (
          <Box sx={{ p: 2.25, pb: 5, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Subjects by Grade — the most important mapping, shown FIRST (before
                the summary cards) so the full grade→subject relationship is visible
                near the top without scrolling. Shared canonical presentation. */}
            {/* Subjects by Grade — shared adaptive primitive. Small
                collections render inline; broad multi-grade classes
                collapse to a counts + preview + "View All" affordance
                that opens the shared SubjectDetailsDialog, so this
                card never destroys the drawer's vertical hierarchy. */}
            <Card icon={<MenuBookOutlinedIcon />} title="Subjects by Grade" tint="#6366f1">
              <GradeSubjectsSummary
                groups={d.grade_subjects as any}
                modalTitle="Subjects by Grade"
              />
            </Card>

            {/* Summary stat cards */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1.25 }}>
              <Stat icon={<CircleIcon />} value={status?.label ?? d.session.status} label="Status" tint={status?.color ?? primary} />
              <Stat icon={<AccessTimeOutlinedIcon />} value={fmtDuration(d.session.duration_minutes)} label="Duration" tint={primary} />
              <Stat icon={<GroupsOutlinedIcon />} value={`${d.class.enrolled}`} label={d.class.enrolled === 1 ? "Student" : "Students"} tint={theme.palette.info?.main ?? "#0ea5e9"} />
              <Stat icon={<EventSeatOutlinedIcon />} value={d.class.available_seats ?? "Not set"} label="Seats left" tint={theme.palette.success.main} />
            </Box>

            {/* 1 — Session details */}
            <Card icon={<InfoOutlinedIcon />} title="Session Details">
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.75 }}>
                <Detail label="Date">{fmtDateFull(d.session.scheduled_date)}</Detail>
                <Detail label="Time">{fmtTime(d.session.start_time)}–{fmtTime(d.session.end_time)}</Detail>
                <Detail label="Duration">{fmtDuration(d.session.duration_minutes)}</Detail>
                <Detail label="Schedule">{d.session.is_recurring ? "Recurring session" : "One-off session"}</Detail>
                <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                  <Detail label="Last updated">
                    {d.audit.updated_at ? `${fmtDateTime(d.audit.updated_at)}${d.audit.updated_by ? ` by ${d.audit.updated_by}` : ""}` : "—"}
                  </Detail>
                </Box>
              </Box>
            </Card>

            {/* 2 — Class information (no misleading flattened Class name) */}
            <Card icon={<MenuBookOutlinedIcon />} title="Class Information" tint="#0ea5e9">
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.75 }}>
                <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                  <Detail label="Class name">{d.class.display_name || "Multi-grade scheduled class"}</Detail>
                </Box>
                <Detail label="Class Type">{softChip(d.class.class_type || "—", "#64748b")}</Detail>
                <Detail label="Enrollment Type"><Box sx={{ textTransform: "capitalize" }}>{d.class.program_type || "—"}</Box></Detail>
                <Detail label="Study Mode">
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
                    {d.class.is_online ? <VideocamOutlinedIcon sx={{ fontSize: 15, color: "#0ea5e9" }} /> : <LocationOnOutlinedIcon sx={{ fontSize: 15, color: "#8b5cf6" }} />}
                    {softChip(d.class.is_online ? "Online" : "In Person", d.class.is_online ? "#0ea5e9" : "#8b5cf6")}
                  </Box>
                </Detail>
                <Detail label="Location">{d.class.is_online ? "Not applicable" : (d.class.location || "—")}</Detail>
                <Detail label="Capacity">{d.class.capacity ?? "—"}</Detail>
                <Detail label="Enrollment">{softChip(d.class.enrollment_status, enrollTint(d.class.enrollment_status))}</Detail>
              </Box>
            </Card>

            {/* Staff Overview — compact companion to the header. Just a
                "View Profile" affordance + a "Previously assigned"
                callout when a mid-series staff switch happened. The full
                staff identity has already been established at the top of
                the drawer; the standalone Staff card that used to sit
                here was redundant. */}
            {(gViewStaff.on || d.staff?.previous_staff) && (
              <Card icon={<BadgeOutlinedIcon />} title="Staff Overview" tint="#0ea5e9">
                {d.staff?.name ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.25 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: "1 1 200px" }}>
                      <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: alpha(primary, 0.15), color: primary }}>{initials(d.staff.name)}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.staff.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: SLATE }}>Assigned to this occurrence{d.staff.location ? ` · ${d.staff.location}` : ""}</Typography>
                      </Box>
                    </Box>
                    {gViewStaff.on && (
                      <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "15px !important" }} />} onClick={() => onAction?.("viewStaff", d.staff.id)} sx={{ textTransform: "none", fontSize: 11.5, borderRadius: "8px" }}>View Profile</Button>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, color: SLATE }}>
                    <PersonOffOutlinedIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ fontSize: 13 }}>No staff assigned. Use Switch Staff above to assign one.</Typography>
                  </Box>
                )}
                {d.staff?.previous_staff && (
                  <Box sx={{ mt: 1.25, px: 1.25, py: 0.75, borderRadius: "8px", bgcolor: alpha("#f59e0b", 0.08), border: `1px solid ${alpha("#f59e0b", 0.2)}` }}>
                    <Typography sx={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>Previously assigned: {d.staff.previous_staff}</Typography>
                  </Box>
                )}
              </Card>
            )}

            {/* 5 — Students */}
            <Card icon={<PeopleAltOutlinedIcon />} title={`Students · ${d.students?.length ?? 0} of ${d.class.capacity ?? "—"} enrolled`} tint="#22c55e"
              action={gAssign.on ? (
                <Button size="small" startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: "15px !important" }} />} onClick={() => onAction?.("assignStudent")} sx={{ textTransform: "none", fontSize: 11.5 }}>Assign</Button>
              ) : undefined}
            >
              {/* capacity progress */}
              {d.class.capacity != null && (
                <Box sx={{ mb: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.round(((d.class.enrolled || 0) / Math.max(1, d.class.capacity)) * 100))}
                    sx={{ height: 6, borderRadius: 4, bgcolor: alpha("#22c55e", 0.14), "& .MuiLinearProgress-bar": { bgcolor: d.class.enrolled >= d.class.capacity ? "#ef4444" : "#22c55e", borderRadius: 4 } }}
                  />
                </Box>
              )}
              {d.students?.length ? (
                <Box sx={{ ...(d.students.length > 6 ? { maxHeight: 300, overflowY: "auto" } : {}), mx: -0.5, px: 0.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {d.students.map((s: any) => (
                    <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: "10px", bgcolor: alpha(NAVY, 0.02), border: "1px solid rgba(15,23,42,0.05)" }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: alpha("#22c55e", 0.15), color: "#15803d" }}>{initials(s.name)}</Avatar>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: SLATE }}>{[s.reference, s.grade].filter(Boolean).join(" · ") || "—"}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: SLATE }}>Attendance: {s.attendance_status ? <Box component="span" sx={{ fontWeight: 700, textTransform: "capitalize", color: NAVY }}>{s.attendance_status}</Box> : "Not recorded"}</Typography>
                      </Box>
                      {gViewStudent.on && (
                        <Tooltip title="View student">
                          <IconButton size="small" aria-label={`View ${s.name}`} onClick={() => onAction?.("viewStudent", s.id)}><VisibilityOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: alpha(NAVY, 0.05), display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.25 }}>
                    <PersonOffOutlinedIcon sx={{ fontSize: 24, color: MUTED }} />
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: SLATE, mb: gAssign.on ? 1.5 : 0 }}>No students assigned to this session.</Typography>
                  {gAssign.on && (
                    <Button size="small" variant="outlined" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => onAction?.("assignStudent")} sx={{ textTransform: "none", borderRadius: "9px" }}>Assign Student</Button>
                  )}
                </Box>
              )}
            </Card>

            {/* 6 — Recurrence */}
            {d.recurrence?.is_recurring ? (
              <Card icon={<EventRepeatOutlinedIcon />} title="Recurrence" tint="#f59e0b">
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.75, mb: 1.5 }}>
                  <Detail label="Frequency">{d.recurrence.type}</Detail>
                  <Detail label="Days">{(d.recurrence.days || []).join(", ") || "—"}</Detail>
                  <Detail label="Start date">{d.recurrence.start_date ? dayjs(d.recurrence.start_date).format("MMM D, YYYY") : "—"}</Detail>
                  <Detail label="End date">{d.recurrence.end_date ? dayjs(d.recurrence.end_date).format("MMM D, YYYY") : "—"}</Detail>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }, gap: 1 }}>
                  {[
                    { v: d.recurrence.generated_count, l: "Generated", t: primary },
                    { v: d.recurrence.remaining_future, l: "Remaining", t: theme.palette.success.main },
                    { v: d.recurrence.cancelled_count, l: "Cancelled", t: theme.palette.error.main },
                    { v: d.recurrence.rescheduled_count, l: "Rescheduled", t: "#f59e0b" },
                    // Batch 15 — surface the not_started status the Batch 11
                    // detector writes, so this summary doesn't silently lose
                    // those sessions. Deep amber, visually distinct from
                    // Cancelled (red) and Rescheduled (light amber).
                    { v: d.recurrence.not_started_count, l: "Not Started", t: "#ea580c" },
                    { v: d.recurrence.exceptions_count, l: "Exceptions", t: "#8b5cf6" },
                  ].map((s, i) => (
                    <Box key={i} sx={{ bgcolor: alpha(s.t, 0.06), border: `1px solid ${alpha(s.t, 0.15)}`, borderRadius: "10px", p: 1, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: s.t, lineHeight: 1 }}>{s.v ?? 0}</Typography>
                      <Typography sx={{ fontSize: 10, color: SLATE, fontWeight: 600, mt: 0.25 }}>{s.l}</Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderRadius: "10px", bgcolor: alpha(primary, 0.05), border: `1px solid ${alpha(primary, 0.12)}` }}>
                <EventRepeatOutlinedIcon sx={{ fontSize: 16, color: primary }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>One-off session</Typography>
              </Box>
            )}

            {/* 7 — Notes & activity */}
            <Card icon={<NotesOutlinedIcon />} title="Notes & Activity" tint="#8b5cf6">
              <Box sx={{ px: 1.5, py: 1.25, borderRadius: "10px", bgcolor: alpha("#8b5cf6", 0.05), border: `1px solid ${alpha("#8b5cf6", 0.14)}`, mb: 1.75 }}>
                {d.notes?.class_notes ? (
                  <Typography sx={{ fontSize: 12.5, color: NAVY }}>{d.notes.class_notes}</Typography>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: SLATE, fontStyle: "italic" }}>No notes added.</Typography>
                )}
                {d.notes?.cancellation_reason && (
                  <Typography sx={{ fontSize: 12, color: theme.palette.error.main, mt: 0.75 }}>Cancellation: {d.notes.cancellation_reason}</Typography>
                )}
                {d.notes?.override_reason && (
                  <Typography sx={{ fontSize: 12, color: "#b45309", mt: 0.5 }}>Override: {d.notes.override_reason}</Typography>
                )}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.75 }}>
                <Detail label="Created">{d.audit.created_at ? `${fmtDateTime(d.audit.created_at)}${d.audit.created_by ? ` by ${d.audit.created_by}` : ""}` : "—"}</Detail>
                <Detail label="Last modified">{d.audit.updated_at ? `${fmtDateTime(d.audit.updated_at)}${d.audit.updated_by ? ` by ${d.audit.updated_by}` : ""}` : "—"}</Detail>
              </Box>
            </Card>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ScheduleDetailsDrawer;
